import { notAvailableValue, SortKey, UtilsService } from './utils.service';
import { Observable, throwError } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { BackendService } from '@services';
import {
    IMergeResponse,
    IMpiRecord,
    ISearchParam,
    ISearchResponse,
    ISplitResponse,
    MpiStorageKey,
} from './search.config';
import { map, catchError } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { formatPhone } from '@app/shared/utility/functions/formatting/format-phone.function';
import { MciCompareRecordsModalComponent } from '@app/containers/mci/shared/compare-records/compare-records-modal.component';
import {
    IListProfileBoosterAttributesByMpiId,
    IIdentityDetail,
    IHistoryTransaction,
    IBestDetails,
} from '@containers/mci/search/details/search-details.config';
import { RecordsActions } from '@app/store';
import { AuthStore } from '@app/store/auth.store';
import { AppPermissions } from '@core/services/acl/config/acl-permissions-constants';
import { mpiConfig } from '@app/core/config/app-config.constants';
import { unset } from 'lodash';
import { IPagination } from '@app/core/data-models/common/pagination.interface';
import { Store } from '@ngrx/store';

@Injectable({
    providedIn: 'root',
})
export class SearchService extends BackendService {
    readonly dialog = inject(MatDialog);
    private utilsService = inject(UtilsService);
    private authStore = inject(AuthStore);
    private readonly store = inject(Store);

    /**
     * Get identity details by mpiId
     *
     * @param mpiId
     * @returns Observable<IIdentityDetail>
     */
    getIdentityDetails(mpiId: string): Observable<IIdentityDetail> {
        const userHasPermissionToPB = this.authStore.hasPermissionToPB();
        const userHasPermissionToBestDetails = this.authStore.hasPermissions([AppPermissions.mci.bestIdentity]);
        const userHasPermissionToMortalityInsights = this.authStore.hasPermissions([
            AppPermissions.mci.mortalityInsights,
        ]);
        const userHasPermissionToHouseholdMembers = this.authStore.hasPermissions([
            AppPermissions.mci.householdMembers,
        ]);

        return this.doGet(mpiConfig.api.mciElementByMciId(mpiId)).pipe(
            map((response) => {
                const data = response.data;
                return {
                    ...data,
                    mpiRecords: this.utilsService.setPrincipalAddress(data.mpiRecords),
                    bestDetailsAvailable: userHasPermissionToBestDetails === false ? false : data.bestDetailsAvailable,
                    profileBoosterAttributesAvailable:
                        userHasPermissionToPB === false ? false : data.profileBoosterAttributesAvailable,
                    mortalityInsightsAvailable:
                        userHasPermissionToMortalityInsights === false ? false : data.mortalityInsightsAvailable,
                    householdMemberReportAvailable:
                        userHasPermissionToHouseholdMembers === false ? false : data.householdMemberReportAvailable,
                };
            }),
        );
    }

    /**
     * Get profile booster attributes by mpiIds
     *
     * @param mpiIds
     * @returns Observable<IListProfileBoosterAttributesByMpiId>
     */
    getProfileBoosterInBatch(mpiIds: string[]): Observable<IListProfileBoosterAttributesByMpiId> {
        return this.doPost(mpiConfig.api.mciProfileBoosterInBatch, JSON.stringify({ mpiIds })).pipe(
            map((response) => response.data),
        );
    }

    /**
     * Get record details by record ID
     *
     * @param recordId - MPI record ID to fetch details for
     * @returns Observable<IMpiRecord | null> - The record found or null
     */
    getRecordByRecordId(recordId: number): Observable<IMpiRecord | null> {
        return this.doGet(mpiConfig.api.mciSearchByRecordId(recordId)).pipe(map((response) => response.data ?? null));
    }

    /**
     * Get history Merge/Split records
     *
     * @param mpiId
     * @returns Observable<IHistoryTransaction[]>
     */
    getHistoryRecords(mpiId: string): Observable<IHistoryTransaction[]> {
        return this.doGet(mpiConfig.api.mciHistoryRecordsByMciRecordId(mpiId)).pipe(
            map((response) =>
                this.utilsService.sortKey<IHistoryTransaction>(
                    [...response.data],
                    SortKey.dateAddedToHistoryRecords,
                    false,
                ),
            ),
        );
    }

    /**
     * Get pending transactions
     *
     * @returns Observable<IMpiRecord[]>
     */
    getPendingTransactions(params: IPagination): Observable<any> {
        const payload: Partial<IPagination> = {
            page: params.page,
            size: params.size,
        };

        if (params.sort && params.sortDirection) {
            payload.sort = `${params.sort},${params.sortDirection}`;
            unset(payload, 'sortDirection');
        }

        if (params.filter) {
            payload.filter = params.filter;
        }

        const queryString = new URLSearchParams(
            Object.entries(payload).reduce(
                (acc, [key, value]) => {
                    if (value !== undefined) {
                        acc[key] = String(value);
                    }
                    return acc;
                },
                {} as Record<string, string>,
            ),
        ).toString();
        const urlWithParams = `${mpiConfig.api.mciPendingTransactions}?${queryString}`;

        return this.doGet(urlWithParams).pipe(
            map((response) => ({
                data: response.data ?? [],
                pagination: response.pagination ?? {},
            })),
        );
    }

    /**
     * Search for MCI
     *
     * @param params
     * @returns Observable<ISearchResponse>
     */
    search(params?: ISearchParam): Observable<IMpiRecord[]> {
        const payload = params
            ? JSON.stringify(params)
            : this.utilsService.getDataFromStorage(MpiStorageKey.lastSearchRequest);

        this.utilsService.setDataInStorage(MpiStorageKey.lastSearchRequest, payload);
        return this.doPost(mpiConfig.api.mciSearch, payload).pipe(
            map((response) => this.utilsService.setPrincipalAddress(response.data || [])),
        );
    }

    /**
     * Get records by householdId
     *
     * @param mpiHouseholdId
     * @returns Observable<IMpiRecord[]>
     */
    getRecordsByHouseholdId(mpiHouseholdId: string): Observable<IMpiRecord[]> {
        return this.doPost(mpiConfig.api.mciSearch, JSON.stringify({ mpiHouseholdId })).pipe(
            map((response) => this.utilsService.setPrincipalAddress(response.data || [])),
        );
    }

    /**
     * Merge elements by mpiIds
     *
     * @param mpiIds
     * @returns Observable<IMergeResponse>
     */
    merge(mpiIds: string[]): Observable<IMergeResponse> {
        const payload = JSON.stringify({ mpiIds });
        return this.doPost(mpiConfig.api.mciMerge, payload).pipe(
            map((response) => this.handleSuccessRespsonse<IMergeResponse>(response)),
            catchError((error, obs) => this.handleErrorRersponse(error)),
        );
    }

    /**
     * Split elements by mpiRecordIds
     *
     * @param toMpiId
     * @param sourceRids
     * @returns Observable<ISplitResponse>
     */
    split(identity: IMpiRecord, transactions: IMpiRecord[]): Observable<ISplitResponse> {
        const toMpiId = identity.mpiId !== notAvailableValue ? identity.mpiId : null;
        const sourceRids = transactions.map((transaction) => transaction.sourceRid);
        const allRecords = !!toMpiId ? [identity, ...transactions] : transactions;
        const mpiIds = allRecords.map((record) => record.mpiId);
        const payload = JSON.stringify({ toMpiId, sourceRids, mpiIds });
        return this.doPost(mpiConfig.api.mciSplit, payload).pipe(
            map((response) => this.handleSuccessRespsonse<ISplitResponse>(response)),
            catchError((error, obs) => this.handleErrorRersponse(error)),
        );
    }

    /**
     * Get main phone from phone list
     *
     * @param item
     * @returns string
     */
    getMainPhone(phones: any[]): string {
        if (!phones.length) {
            return '';
        }
        const phone = phones[0].phone || '';
        return formatPhone(phone);
    }

    /**
     * Open compare dialog
     *
     * @param records
     * @param isPublicRecord
     * @private
     */
    openCompareDialog(records: IMpiRecord[] | IBestDetails[], isPublicRecord: boolean = false): void {
        this.dialog.open(MciCompareRecordsModalComponent, {
            minWidth: '1024px',
            maxWidth: '75vw !important',
            width: '75vw',
            data: {
                isPublicRecord,
                records: records.map((record) => this.mergeRecordZipcode(record)),
            },
        });
    }

    /**
     * Merges the record's zipcode with the zip4 extension if it exists
     *
     * @param record - The record to merge, which can be of type IMpiRecord or IBestDetails
     * @returns The merged record with the updated zipcode
     */
    private mergeRecordZipcode<T extends IMpiRecord | IBestDetails>(record: T): T {
        const { address } = record;
        if (!Boolean(address?.zip4)) {
            return record;
        }
        return {
            ...record,
            address: {
                ...address,
                zipCode: `${address.zipCode}-${address.zip4}`,
            },
        };
    }

    /**
     * Handle the success response
     *
     * @param response
     * @param clearResult
     * @returns IMpiRecord[] | string
     */
    private handleSuccessRespsonse<T = IMpiRecord[] | string>(response: ISearchResponse): T {
        const data = response.data;
        this.store.dispatch(RecordsActions.clearSearchResult());
        return data as T;
    }

    /**
     * Handle an error response
     *
     * @param error the error of the request
     * @return Observable<never>
     */
    private handleErrorRersponse(error: any): Observable<never> {
        this.store.dispatch(RecordsActions.clearSearchResult());
        return throwError(() => error);
    }
}
